// Pure data + helpers for the left sidebar.
//
// All sections are populated from this module so the React component
// stays a thin renderer and the composition + role gating unit-test
// without a DOM.
//
// The navigation overhaul (Grant, 2026-10-15; drawing:
// docs/design/navigation-overhaul-mockup-v1.html) reorganized the rail
// around one rule: every row lives where its KIND of work lives, never
// under a person's name. The panel headed with the user's first name had
// become the home for anything without one (23 rows for Grant: three
// dashboards, seven Marketing Hub screens, nine company-specific tools,
// a few reference lists). It is now:
//
//   Home         where you land + the work waiting on you (≤ 7 rows)
//   Marketing    every Marketing Hub screen (new icon)
//   Company      each company's own tools, grouped by company (new icon)
//
// and the rest of the rail is what it was, tidied: Admin regrouped by
// job, colliding labels renamed, and every page that had no menu link
// given one.

import { isOpsDeskEmail } from "@shared/ops-desk/members";
import type { RoleAwareUser } from "../utils/role-visibility";
import {
  canSeeAdminSection,
  canSeeApprovedLenders,
  canSeeCallRecordingUpload,
  canSeeCommissionReconciliation,
  canSeeCompanyCoverage,
  canSeeEmailSection,
  canSeeFeeReview,
  canSeeIvansReview,
  canSeeMortgageCompliance,
  canSeeMortgageWatch,
  canSeePlanningBook,
  canSeePlanningDeposits,
  canSeeFinancialBilling,
  canSeeStructuredNotes,
  canSeeTeamPage,
} from "../utils/role-visibility";
import { visibleNavItems } from "./top-nav-helpers";
import { mayReachPmGuidelines } from "./user-menu-helpers";

// Every icon the rail and its panels may draw. Sidebar.tsx maps each name
// to its lucide-react component in a Record<IconName, …>, so a name added
// here without an icon (or the reverse) is a type error, not a blank
// square on screen.
export type IconName =
  | "activity"
  | "archive"
  | "badge-check"
  | "banknote"
  | "bar-chart"
  | "bookmark"
  | "book-open"
  | "briefcase"
  | "building"
  | "building-2"
  | "calculator"
  | "calendar"
  | "calendar-check"
  | "calendar-cog"
  | "chat"
  | "check-circle"
  | "check-square"
  | "circle-dot"
  | "clipboard-list"
  | "clock"
  | "columns"
  | "compass"
  | "cpu"
  | "data-hygiene"
  | "draft"
  | "eye"
  | "file-chart"
  | "file-text"
  | "folder"
  | "folder-check"
  | "gauge"
  | "graduation-cap"
  | "hand-coins"
  | "handshake"
  | "heart-handshake"
  | "home"
  | "id-card"
  | "inbox"
  | "junk"
  | "kanban"
  | "key"
  | "landmark"
  | "layout-dashboard"
  | "layout-grid"
  | "layout-list"
  | "library"
  | "life-buoy"
  | "line-chart"
  | "link"
  | "list"
  | "list-checks"
  | "list-filter"
  | "lock"
  | "mail"
  | "mail-check"
  | "mail-open"
  | "mails"
  | "megaphone"
  | "message-square"
  | "star"
  | "mic"
  | "network"
  | "notebook-pen"
  | "outbox"
  | "package"
  | "pen"
  | "pen-tool"
  | "percent"
  | "plug"
  | "radio-tower"
  | "receipt"
  | "route"
  | "scan-search"
  | "send"
  | "settings"
  | "settings-2"
  | "share"
  | "shield"
  | "shield-check"
  | "sliders"
  | "sun"
  | "target"
  | "team"
  | "trash"
  | "trending-up"
  | "type"
  | "umbrella"
  | "upload"
  | "user"
  | "users"
  | "wallet"
  | "workflow";

export interface SidebarItem {
  label: string;
  to: string;
  icon: IconName;
  badge?: string;
  // Marks a still-placeholder route — SidebarLink shows a small
  // "Preview" pill (audit PR 11; mirrors the ⌘K preview tag).
  preview?: boolean;
  // Key into CORE_VOCABULARY (core-vocabulary.ts) for the inline "what
  // is this?" `?` affordance next to the label. A plain string (not the
  // definition text) so the definitions live in one place. Omit to
  // render no help glyph.
  help?: string;
}

// A labeled cluster of items inside a section. Admin, Marketing and
// Company tools use these to break a long panel into readable groups.
export interface SidebarSubgroup {
  heading: string;
  items: SidebarItem[];
  // Start this group folded. Admin's groups always do (the "Admin
  // groups" preference decides); a Company tools group does only for
  // people who serve all five companies, so their panel opens short
  // while everyone else sees their one or two companies open.
  startFolded?: boolean;
}

export interface SidebarSection {
  // Heading shown at the top of the open panel.
  heading: string | null;
  items: SidebarItem[];
  // When present, the section renders these labeled subgroups instead of
  // a flat `items` list (see Sidebar.tsx). `items` stays [] in that case.
  subgroups?: SidebarSubgroup[];
  // Stable identifier, independent of the display heading. Used by the
  // icon rail (which section is open), the "open on my last section"
  // preference, and the reachability test. "me" is Home's key: it was
  // the panel headed with the user's name, and keeping the key means a
  // saved "last section" still opens the same panel.
  key: SectionKey;
  // Icon shown in the always-visible rail.
  icon: IconName;
  // The word under the rail icon when the heading is too long for the
  // 68px button ("COMPANY TOOLS" → "Company"). Defaults to the heading.
  railLabel?: string;
}

// The rail, in Grant's order (2026-10-15):
//   Home · Email · Tasks · Calendar · Marketing · Automations · Forms ·
//   E-sign · Company tools · Admin
// This list IS the rail: a section that isn't one of these has nowhere
// to render, which forces every new destination to be placed under one
// of them rather than growing another heading nobody scans.
export type SectionKey =
  | "me"
  | "email"
  | "tasks"
  | "calendar"
  | "marketing"
  | "automations"
  | "forms"
  | "esign"
  | "company"
  | "admin";

// ── Home ────────────────────────────────────────────────────────────
//
// Where you land, then what is queued for you. Nothing else: a row that
// is a company's tool goes to Company tools, a Marketing Hub screen goes
// to Marketing. That rule is what keeps this panel short.
//
// "Dashboard" first for everyone: /dashboard dispatches each person to
// their OWN tailored view (pickDashboardForUser), so it is the page they
// actually land on after signing in.
const FOUNDER_SHORTCUTS: SidebarItem[] = [
  { label: "Dashboard", to: "/dashboard", icon: "layout-dashboard" },
  // Renamed from "Pulse detail" (2026-10-15): the page is the firm's
  // 12-month trend charts, and "pulse detail" meant nothing to anyone who
  // hadn't been told.
  { label: "Firm trends", to: "/pulse", icon: "line-chart" },
  { label: "Reports", to: "/reports", icon: "file-chart", help: "reports" },
];

// Brandon = co-founder + Insurance advisor. His Home keeps the two
// Insurance shortcuts he was given (his Insurance dashboard and the
// renewal slice of Ongoing Care) — per-user tailoring, not a pattern for
// everyone.
const BRANDON_SHORTCUTS: SidebarItem[] = [
  { label: "Dashboard", to: "/dashboard", icon: "layout-dashboard" },
  { label: "Insurance", to: "/dashboard/insurance", icon: "shield" },
  {
    label: "Insurance Renewals",
    to: "/ongoing-care?f=company:insurance&f=type:renewal_plan",
    icon: "activity",
  },
  { label: "Reports", to: "/reports", icon: "file-chart", help: "reports" },
];

// Everyone else. "Today" (My Day, a simpler card launchpad) stays — a
// real, distinct page — but second, because /dashboard is the landing.
const DEFAULT_SHORTCUTS: SidebarItem[] = [
  { label: "Dashboard", to: "/dashboard", icon: "layout-dashboard" },
  { label: "Today", to: "/my-day", icon: "sun" },
  { label: "Reports", to: "/reports", icon: "file-chart", help: "reports" },
];

// CLIENTS group — deeper record views. On desktop the six record lanes
// live in the top bar, so this is built only for back_office (below),
// whose Home would otherwise hold nothing about the policy work she does.
const CLIENTS_ITEMS: SidebarItem[] = [
  // Labeled "Policies & Services" (not the operating-layer "Service
  // Records") per the vocabulary lock — Joy, 2026-07-01.
  { label: "Policies & Services", to: "/service-records", icon: "list", help: "service-records" },
  {
    label: "Opportunity Board",
    to: "/opportunities/board",
    icon: "circle-dot",
    help: "opportunity-board",
  },
  {
    label: "Opportunity Aging",
    to: "/opportunities/aging",
    icon: "activity",
    help: "opportunity-aging",
  },
  {
    label: "Opportunity Forecast",
    to: "/opportunities/forecast",
    icon: "calendar",
    help: "opportunity-forecast",
  },
  {
    label: "Renewals",
    to: "/ongoing-care?f=type:renewal_plan",
    icon: "activity",
    help: "ongoing-care",
  },
  {
    label: "Care Calendar",
    to: "/ongoing-care?view=calendar",
    icon: "calendar",
    help: "ongoing-care",
  },
];

// CLIENTS, per role. Mylah's walkthrough (2026-07-01): the three
// Opportunity analytics views are advisor sales tools she would never
// use, so back_office drops them. This trims the DEFAULT rail only,
// never the reach: the top-nav Opportunities lane and ⌘K still land on
// all three for every role.
export function pickClientsItems(user: RoleAwareUser): SidebarItem[] {
  return user?.employee_role_slug === "back_office"
    ? CLIENTS_ITEMS.filter((i) => !i.to.startsWith("/opportunities/"))
    : CLIENTS_ITEMS;
}

// Per-user-id dashboard-owner overrides. Canonical UUIDs come from
// migration/activecampaign/output/sql/01-users.sql. If a user id ever
// changes in the seed, keep this in lock-step with
// web/src/pages/DashboardPage.tsx's BRANDON_ADVISOR_ID.
const BRANDON_USER_ID = "cd53d0da-c6d2-5e3d-860d-c7515426d80f";

// The top of Home, chosen by identity → role, in that order. Exported for
// the unit test that asserts Brandon keeps his tailored set while other
// company_admins get the default.
export function pickShortcuts(user: RoleAwareUser): SidebarItem[] {
  if (user?.id === BRANDON_USER_ID) return BRANDON_SHORTCUTS;
  if (user?.employee_role_slug === "system_admin") return FOUNDER_SHORTCUTS;
  return DEFAULT_SHORTCUTS;
}

function buildHomeItems(user: RoleAwareUser): SidebarItem[] {
  const items: SidebarItem[] = [
    ...pickShortcuts(user),
    // The operations desk (build step C1): Baker and Grant, and Cambria and
    // Donna for the duties named for them. Person-scoped, by the desk list;
    // the server checks the same list.
    ...(isOpsDeskEmail(user?.email) ? [{ label: "Operations desk", to: "/desk", icon: "clipboard-list" } satisfies SidebarItem] : []),
    // The viewer's own Category-6 judgment calls plus their cleanup lane.
    { label: "Data Needed", to: "/data-needed", icon: "check-circle", help: "data-needed" },
    // The judgment-completion scoreboard — every employee, per the
    // transparency doctrine.
    { label: "Team progress", to: "/data-needed/team", icon: "users" },
  ];
  // back_office (Mylah) gets no Email / Tasks / Calendar and no Admin, so
  // without these her Home would hold nothing about her policy work.
  if (user?.employee_role_slug === "back_office") items.push(...pickClientsItems(user));
  return items;
}

// ── Email ───────────────────────────────────────────────────────────
//
// EMAIL's body is rendered LIVE by EmailSidebarSection (the real folder
// tree from /api/email/folders). These are the email destinations that
// are NOT mailbox folders; EmailSidebarSection imports and renders them
// itself, directly under its Search row and above the folder tree.
//
// DECISION (2026-08-07): the section's items must be rendered by the
// component that OWNS the section, not merely handed to Sidebar.tsx,
// which swaps the whole EMAIL body out — anything left only here would
// be built and thrown away (how Batch Sends and Bulk Email went
// invisible for months).
//
// The marketing blast left this list 2026-10-15: it is a marketing send,
// so it lives in Marketing → Studio. Batch Email to Clients stays,
// because it sends from the person's own Outlook mailbox.
export const EMAIL_DESTINATION_ITEMS: SidebarItem[] = [
  // Outlook paced batch sends — progress + cancel for "Email selected"
  // sends started from a contact list (renamed for Grant, 2026-08-10).
  { label: "Batch Email to Clients", to: "/email/batch-sends", icon: "mails" },
];

// ── Tasks ───────────────────────────────────────────────────────────
//
// Checklists joined 2026-10-15 from the avatar menu (Grant approved the
// drawing that moved it): a checklist is a list of things to get done,
// which is what this panel holds. Every employee may open it; who may
// EDIT one is still decided on the page and by the server.
const TASKS_ITEMS: SidebarItem[] = [
  { label: "My Tasks", to: "/tasks", icon: "check-square" },
  { label: "Checklists", to: "/checklists", icon: "list-checks" },
];

// ── Calendar ────────────────────────────────────────────────────────
//
// "My Calendar" is the two-way Outlook proxy. The booking rows joined
// 2026-08-25: an advisor's own schedule page was reachable only from a
// column header on the front-desk board, and the booking settings only
// via avatar menu → Settings → third tab.
const CALENDAR_ITEMS: SidebarItem[] = [
  { label: "My Calendar", to: "/calendar", icon: "calendar" },
  { label: "Appointments", to: "/appointments", icon: "calendar-check" },
  // "me" keeps this data file static; AdvisorSchedulePage resolves it to
  // the signed-in user.
  { label: "My Booking Page", to: "/advisors/me/schedule", icon: "link" },
  // A door into Settings' booking tabs, not a copy of them.
  { label: "Booking Settings", to: "/settings?tab=booking", icon: "calendar-cog" },
];

// ── Marketing ───────────────────────────────────────────────────────
//
// New icon 2026-10-15, between Calendar and Automations as Grant asked.
// The Marketing Hub plan (docs/design/marketing-hub.md §7) calls for ONE
// Marketing area; until now its screens were seven loose rows added to
// every person's own panel, and Founder Studio was in no menu at all.
//
// Every employee reads their own companies' rows on each of these pages
// (the pages scope themselves); Founder Studio and Marketing settings are
// system_admin only, the same gate the pages and the route index use.
// Rules a marketing screen relies on are still built in Automations: one
// engine, so people always know what fires.
function buildMarketingSubgroups(user: RoleAwareUser): SidebarSubgroup[] {
  const studio: SidebarItem[] = [];
  if (canSeeTeamPage(user)) {
    studio.push({ label: "Founder Studio", to: "/marketing/studio/founder", icon: "pen-tool" });
    // Row 7.9a: the approval queue. Grant acts and Baker reads; Jake and
    // Ryan reach their own sign-offs from their Today.
    studio.push({ label: "Approvals", to: "/marketing/approvals", icon: "check-circle" });
  }
  studio.push({ label: "Share packages", to: "/marketing/studio/share", icon: "share" });
  // Row 8.2: the social composer. Every employee reads; the page offers
  // "Write a post" only to someone with a company desk (or Grant).
  studio.push({ label: "Social posts", to: "/marketing/posts", icon: "send" });
  // Row 8.4: the Calendar (every scheduled and sent piece, every channel)
  // and the Content library. Every employee reads; the pages scope
  // themselves to the viewer's companies.
  studio.push({ label: "Calendar", to: "/marketing/calendar", icon: "calendar" });
  studio.push({ label: "Content", to: "/marketing/content", icon: "library" });
  // Row 7.5: every employee reads every company's campaigns (the page
  // offers "New campaign" only for the companies the viewer's desk saves).
  // Row 7.8a: Goals, where a sentence becomes a prepared plan for Grant.
  // Every employee reads; the page offers Prepare only to the family's desk.
  studio.push({ label: "Goals", to: "/marketing/goals/new", icon: "trending-up" });
  studio.push({ label: "Campaigns", to: "/marketing/campaigns", icon: "kanban" });
  // Row 8.10: events as a workflow (invite, register, remind, attend,
  // follow up). Every employee reads their companies'; the page offers
  // "New event" only to a company's desk.
  studio.push({ label: "Events", to: "/marketing/events", icon: "calendar-check" });
  // Row 8.11: mail and handwritten cards as tasks for a person. Every
  // employee sees their own; "Everyone" widens to their companies.
  studio.push({ label: "Mail and cards", to: "/marketing/mail", icon: "mail" });
  // "Email my clients" (2026-10-23): one door for emailing many families,
  // a chooser between the paced send from the person's own Outlook (which
  // only the Contacts list offered) and the bulk (Amazon SES) blast at
  // /communications, which this row used to point at directly as "Email
  // blasts". Same audience as the Email section the blast came from:
  // back_office never sends client email.
  if (canSeeEmailSection(user)) {
    studio.push({ label: "Email my clients", to: "/marketing/email-clients", icon: "megaphone" });
  }

  // Row 7.10a: Results, each goal against its brief. Every employee.
  const results: SidebarItem[] = [
    { label: "Results", to: "/marketing/results", icon: "target" },
    { label: "Leaderboard", to: "/marketing/leaderboard", icon: "trending-up" },
    { label: "Content results", to: "/marketing/results?tab=content", icon: "bar-chart" },
  ];
  if (canSeeTeamPage(user)) {
    // The Settings home (row 7.12a, screen 57): audiences, the rules Grant
    // sets, and the money pages, each a tab on the page.
    results.push({
      label: "Marketing settings",
      to: "/marketing/settings",
      icon: "sliders",
    });
  }

  return [
    {
      heading: "Families",
      items: [
        // The hub's Today, labelled so it can't be confused with Home →
        // Today (My Day).
        { label: "Next Best Help", to: "/marketing/today", icon: "heart-handshake" },
        // The gaps, counted four ways.
        { label: "Where we could help", to: "/marketing/opportunities", icon: "target" },
        // Row 7.8b: each family's growth level (everyone reads; Grant changes).
        { label: "Growth levels", to: "/marketing/levels", icon: "sliders" },
        // Row 11.3: each product's playbook and outside research.
        { label: "Playbooks", to: "/marketing/playbooks", icon: "book-open" },
        // Row 11.6: plays Grant approves once, launched by each desk.
        { label: "Plays", to: "/marketing/plays", icon: "route" },
        // The referral engine as a work list.
        { label: "Referrals", to: "/marketing/referrals", icon: "handshake" },
        // Where business comes from and what the network produced.
        { label: "Referral Network", to: "/marketing/referral-network", icon: "network" },
        // Every reply, comment, message, review and introduction in one
        // queue. Was "Inbox", easy to mistake for the email inbox.
        { label: "Marketing inbox", to: "/marketing/inbox", icon: "message-square" },
        // Row 8.9: the firm's Google reviews, answered through Grant's approval.
        { label: "Google reviews", to: "/marketing/reviews", icon: "star" },
      ],
    },
    { heading: "Studio", items: studio },
    { heading: "Results", items: results },
  ];
}

// ── Automations ─────────────────────────────────────────────────────
//
// Everything that acts on its own, under one heading (Grant, 2026-08-23).
// Every employee may OPEN all of these: looking at what fires is not an
// authoring permission; the permission matrix gates what you can BUILD,
// server-side. The wording library is the exception — it edits what
// other people's rules send, so it stays admin-side.
function buildAutomationItems(user: RoleAwareUser): SidebarItem[] {
  const items: SidebarItem[] = [
    // Admins manage every rule; non-admins see only rules they created
    // (server-scoped to owner), hence the different label.
    canSeeAdminSection(user)
      ? { label: "All Automations", to: "/automations", icon: "workflow" }
      : { label: "My Automations", to: "/automations", icon: "workflow" },
    // "What's running on my book?" — read-only and cross-creator.
    { label: "Running on my clients", to: "/automations/on-my-clients", icon: "users" },
    // Upcoming Communications (2026-10-15 joined the rail): the dated
    // list behind the dashboard's "Automation · next 7 days" tile. It had
    // no menu link and no search entry; the only way in was that tile.
    { label: "About to go out", to: "/upcoming-communications", icon: "clock" },
    // The scheduled-job registry — the code-side half of the same
    // question the rules answer.
    { label: "What Runs Automatically", to: "/system-jobs", icon: "cpu" },
  ];
  if (canSeeAdminSection(user)) {
    // Message wording never lives in code (automation-first doctrine rule
    // 2). Renamed from "Templates" so it can't be confused with E-sign's
    // signing packages.
    items.push({
      label: "Email & task wording",
      to: "/admin/communication-templates",
      icon: "type",
    });
  }
  return items;
}

// ── Forms ───────────────────────────────────────────────────────────
// Every employee builds and manages forms. Defaults to "My forms"; the
// page's own scope control widens it.
const FORMS_ITEMS: SidebarItem[] = [{ label: "Forms", to: "/forms", icon: "clipboard-list" }];

// ── E-sign ──────────────────────────────────────────────────────────
// Open to every employee; each page scopes itself to the viewer's own
// company by default. Two rows renamed 2026-10-15 so no label in the
// rail is shared by two different pages.
const ESIGN_ITEMS: SidebarItem[] = [
  { label: "E-sign", to: "/esign", icon: "pen" },
  // The per-company library of blank forms a company signs.
  { label: "Industry documents", to: "/esign/industry-documents", icon: "library" },
  // The saved, ordered packages built from those blanks. Was "Templates".
  { label: "Signing packages", to: "/esign/templates", icon: "package" },
  // What the OS has learned about this company's form captions.
  { label: "What the OS has learned", to: "/esign/learned", icon: "graduation-cap" },
  // Your signature, initials and send defaults. Was "Settings".
  { label: "My signature & defaults", to: "/esign/settings", icon: "settings-2" },
];

// ── Company tools ───────────────────────────────────────────────────
//
// New icon 2026-10-15. Each company's own tools, grouped by company —
// the nine rows that used to sit under the founder's name, the two
// Private Money pages that sat in the avatar menu, and the commission
// report that was its own Commissions icon for Brandon.
//
// Every row keeps the gate it had before (the same helper, the same
// answer), so nobody's reach changes; a group only appears when at
// least one of its rows does. "Default to your desk": a person who works
// in one or two companies sees exactly those; a person who serves all
// five (Grant, Baker, Donna) gets the company groups folded, so the
// panel opens short instead of as a 15-row wall.
function buildCompanySubgroups(user: RoleAwareUser): SidebarSubgroup[] {
  const servesAll = user?.editable_company_slugs === "all";
  const groups: SidebarSubgroup[] = [];
  const add = (heading: string, items: (SidebarItem | false)[]) => {
    const visible = items.filter((i): i is SidebarItem => i !== false);
    if (visible.length > 0) groups.push({ heading, items: visible, startFolded: servesAll });
  };

  add("Mortgage", [
    // The roster tab on /vendors, surfaced by name for mortgage people.
    canSeeApprovedLenders(user) && {
      label: "Approved lenders",
      to: "/vendors?view=approved-lenders",
      icon: "badge-check",
    },
    // The four documents an examiner asks Baker for, in one place.
    canSeeMortgageCompliance(user) && {
      label: "Mortgage Compliance",
      to: "/mortgage/compliance",
      icon: "shield-check",
    },
    // Mike's Private Money underwriting numbers and the KlinkLoans fund
    // (moved from the avatar menu). The server gates every request
    // independently; this is the same scope test the menu used.
    // Row 10.5: credit inquiries on our own borrowers, Mortgage only.
    canSeeMortgageWatch(user) && {
      label: "Mortgage watch",
      to: "/mortgage/watch",
      icon: "eye",
    },
    // Client sign-in (Mortgage OS A2a): the same Mortgage audience as the
    // watch, and the same server rule (decideVerificationDataAccess).
    canSeeMortgageWatch(user) && {
      label: "Client sign-in",
      to: "/mortgage/client-sign-in",
      icon: "lock",
    },
    mayReachPmGuidelines(user?.editable_company_slugs) && {
      label: "PM Guidelines",
      to: "/pm-guidelines",
      icon: "book-open",
    },
    mayReachPmGuidelines(user?.editable_company_slugs) && {
      label: "KlinkLoans Fund",
      to: "/pm-fund",
      icon: "landmark",
    },
  ]);

  add("Insurance", [
    // The IVANS log, then the grading queue. Same server gate for both.
    canSeeIvansReview(user) && {
      label: "Carrier activity",
      to: "/insurance/carrier-activity",
      icon: "radio-tower",
    },
    canSeeIvansReview(user) && {
      label: "Check carrier data",
      to: "/insurance/ivans-review",
      icon: "scan-search",
    },
    // The insurance commission reconciliation report — shared by Grant
    // and Brandon (server gate: system_admin / compliance /
    // company_admin). One home now, instead of Admin for some people and
    // a separate rail icon for others.
    canSeeCommissionReconciliation(user) && {
      label: "Are we getting paid?",
      to: "/admin/commission-reconciliation",
      icon: "receipt",
    },
  ]);

  add("Financial", [
    // A genuine reach limit: /api/structured-notes refuses a caller
    // without Financial scope.
    canSeeStructuredNotes(user) && {
      label: "Structured Notes",
      to: "/structured-notes",
      icon: "notebook-pen",
    },
    // Jake's monthly fee-accuracy run; the server refuses anyone outside
    // the gate.
    canSeeFeeReview(user) && {
      label: "Monthly Fee Review",
      to: "/admin/financial/fee-review",
      icon: "calculator",
    },
    // The custodian balance check (Schwab + Altruist + Betterment) and
    // the commission deposits behind it. Both admin-only routes that had
    // no link anywhere until 2026-10-15.
    canSeeAdminSection(user) && {
      label: "Firm dashboard",
      to: "/admin/financial/firm-dashboard",
      icon: "gauge",
    },
    canSeeAdminSection(user) && {
      label: "Commission deposits",
      to: "/admin/financial/deposits",
      icon: "banknote",
    },
  ]);

  add("Planning", [
    // Ryan's ask: every line Planning writes, through to the cases.
    canSeePlanningBook(user) && {
      label: "Book of Business",
      to: "/planning/book-of-business",
      icon: "briefcase",
    },
    // Planning deposits (commissions step C6, part 2): place each carrier
    // deposit on the cases it pays. Same rule as the server.
    // Financial billing files (commissions step C5): the Financial team,
    // system admins, unscoped compliance; the API enforces the same rule.
    // HELP-OK: one new link for the Financial team; no other article's steps change, and the page has its own article (financial-billing).
    canSeeFinancialBilling(user) && {
      label: "Financial Billing",
      to: "/financial/billing",
      icon: "banknote",
    },
    canSeePlanningDeposits(user) && {
      label: "Planning Deposits",
      to: "/planning/deposits",
      icon: "banknote",
    },
    // Medicare call recordings — planning scope + unscoped roles.
    canSeeCallRecordingUpload(user) && {
      label: "Upload Call Recording",
      to: "/call-recordings/upload",
      icon: "mic",
    },
  ]);

  // The firm-wide rows, open (never folded) for everyone.
  groups.push({
    heading: "Every company",
    items: [
      // The roster of carriers, lenders, title companies and custodians.
      { label: "Vendors", to: "/vendors", icon: "building-2" },
      // Businesses that buy group coverage from us (Group LTC first). An
      // employer is a client for the sale, never a household.
      { label: "Employers", to: "/employers", icon: "briefcase" },
      // The stage editor for Processing / Opportunities / Ongoing Care
      // boards — every employee, company-scoped server-side.
      { label: "Edit boards", to: "/admin/pipeline-boards", icon: "kanban" },
      // Show a licensee's current license on request (moved from the
      // avatar menu; every role may open it).
      { label: "License lookup", to: "/license-lookup", icon: "id-card" },
      ...(canSeeCompanyCoverage(user)
        ? [{ label: "Company Coverage", to: "/company-coverage", icon: "umbrella" as const }]
        : []),
    ],
  });
  return groups;
}

// ── Admin ───────────────────────────────────────────────────────────
//
// Visible only to system_admin + compliance (canSeeAdminSection), and
// every route here carries that same gate. Regrouped by job 2026-10-15:
// the three one-row groups (Data, Compliance, Advanced) are gone, and
// the pages that were search-only or unlinked joined the group they
// belong to.
const ADMIN_SUBGROUPS: SidebarSubgroup[] = [
  {
    heading: "People",
    items: [
      { label: "Team", to: "/admin/team", icon: "team" },
      // What we owe Ambassador Agents, and the rules that decide it.
      { label: "Ambassador Fees", to: "/admin/ambassador-fees", icon: "hand-coins" },
      // Who on the team is overloaded.
      { label: "Workload Planner", to: "/admin/workload-planner", icon: "gauge" },
    ],
  },
  {
    heading: "Money",
    // What Stewardship PAYS its advisors: the month's review/approve/send
    // workflow, the payroll run behind it, and the rate table. Whether
    // the carriers paid Stewardship lives in Company tools → Insurance.
    items: [
      { label: "Commissions", to: "/admin/commissions", icon: "wallet" },
      // Was reachable only from inside Commissions.
      { label: "Commission Payroll", to: "/admin/commissions/payroll", icon: "banknote" },
      // Pay runs (build step B3): shadow runs per payday, beside today's
      // payroll page, and what each person is still owed.
      { label: "Pay runs", to: "/admin/commissions/pay-runs", icon: "calendar-check" },
      { label: "Unpaid earnings", to: "/admin/commissions/unpaid", icon: "hand-coins" },
      // The John Hancock 401(k) file after each payroll (build program 9.5).
      { label: "John Hancock 401(k)", to: "/admin/commissions/john-hancock", icon: "landmark" },
      // Productions' monthly Klink Loans invoice, and the monthly Klinkloans
      // origination report (build program C7). Admin and compliance only on the server.
      { label: "Klink invoices", to: "/admin/money/klink-invoices", icon: "receipt" },
      { label: "Klinkloans report", to: "/admin/money/klinkloans-report", icon: "file-text" },
      // The read-only link that lets the OS read Gusto's processed payrolls
      // (pay-runs program 9.4). System admins only on the server.
      { label: "Connect Gusto", to: "/admin/integrations/gusto", icon: "plug" },
      // Each person's deal, read from their comp plan (build step B2).
      // The old Commission Splits page is read-only history, linked from it.
      { label: "Team Compensation", to: "/admin/team-compensation", icon: "users" },
      { label: "Commission Splits · history", to: "/admin/commission-splits", icon: "percent" },
    ],
  },
  {
    heading: "Compliance",
    items: [
      // One page with company tabs; the checklists/rules/issues workflow
      // is linked from its header.
      { label: "Compliance", to: "/admin/company-compliance", icon: "shield" },
      // These three were search-only until 2026-10-15.
      { label: "Legal holds", to: "/admin/legal-holds", icon: "lock" },
      { label: "Retention schedule", to: "/retention-schedule", icon: "archive" },
      { label: "Exam packs", to: "/admin/exam-packs", icon: "folder-check" },
    ],
  },
  {
    heading: "Setup",
    items: [
      // Financial is the first company live; default lands there.
      { label: "Data Imports", to: "/admin/data-imports/financial", icon: "upload" },
      { label: "View defaults", to: "/admin/view-defaults", icon: "layout-list" },
      // Each company's firm block (legal name, licence, NMLS / CRD / MLS).
      { label: "Firm profiles", to: "/admin/firm-profiles", icon: "building-2" },
      // Dashboard pins, FY targets and metric contributors, as tabs.
      { label: "Dashboards & Targets", to: "/admin/dashboards-targets", icon: "target" },
      // What makes a household "at risk", and how the score is tuned.
      // Neither had a menu link or a search entry until 2026-10-15.
      { label: "Watchlist rules", to: "/admin/watchlist-rules", icon: "list-filter" },
      { label: "At-risk calibration", to: "/admin/at-risk-calibration", icon: "sliders" },
    ],
  },
  {
    heading: "System",
    items: [
      // Firm-wide settings. Renamed from "Settings" so it can never be
      // mistaken for avatar menu → My settings, which is per person.
      { label: "OS settings", to: "/admin/settings", icon: "settings" },
      { label: "Integrations", to: "/admin/integrations", icon: "plug" },
      // Slow-query telemetry — where the database is still slow/heavy.
      { label: "Database Health", to: "/admin/database-health", icon: "data-hygiene" },
      // DMARC reports: who sends email as stewardship.pro.
      { label: "Email Health", to: "/admin/email-health", icon: "mail-check" },
      // The /book routing registry.
      { label: "Booking Routing", to: "/admin/booking-routing", icon: "route" },
      // Questions Ask Stew could not answer: the what-to-write-next list
      // for the help desk. Was search-only as "Stew feedback".
      { label: "Help gaps", to: "/admin/stew-feedback", icon: "life-buoy" },
    ],
  },
];

// "N judgment calls + cleanup items need you" on the Data Needed link,
// for every employee. Since 2026-07-23 (B7) the count actually matches
// that label: judgment_open + my_lane_open from /my-progress. Same
// null/0/999+ behavior.
export function withDataNeededBadge(
  sections: SidebarSection[],
  count: number | null,
): SidebarSection[] {
  return withItemBadge(sections, "/data-needed", count);
}

function withItemBadge(
  sections: SidebarSection[],
  to: string,
  count: number | null,
): SidebarSection[] {
  if (count === null || count === 0) return sections;
  const formatted = count > 999 ? "999+" : count.toLocaleString();
  const tag = (item: SidebarItem): SidebarItem =>
    item.to === to ? { ...item, badge: formatted } : item;
  return sections.map((section) => {
    const next: SidebarSection = {
      ...section,
      items: section.items.map(tag),
    };
    // Items may live in ADMIN subgroups (PR 5), so scan subgroups too —
    // otherwise a badge would never render for admins. Set conditionally
    // so exactOptionalPropertyTypes stays happy (never assign
    // `subgroups: undefined`).
    if (section.subgroups) {
      next.subgroups = section.subgroups.map((g) => ({
        ...g,
        items: g.items.map(tag),
      }));
    }
    return next;
  });
}

export function buildSidebarSections(user: RoleAwareUser): SidebarSection[] {
  // Ambassador Agents (deny-by-default role, migration 0241): the rail
  // shows exactly the surface the role can reach — the referral page.
  // Every other section here points at routes the restricted-role gate
  // 403s for them, and a rail full of dead links reads as a broken app.
  if (user?.employee_role_slug === "ambassador") {
    return [
      {
        key: "me",
        icon: "home",
        heading: "HOME",
        items: [
          { label: "Referrals", to: "/referrals", icon: "users" },
          // Free/busy for Nate and Joey, and booking an appointment for
          // one of their own referrals.
          { label: "Advisor Time", to: "/availability", icon: "calendar" },
        ],
      },
    ];
  }

  const sections: SidebarSection[] = [];

  // ── 1. Home ──
  // Account-level things (Profile, My settings, Security, Help, dark
  // mode) are NOT here: they live in the avatar menu, top right.
  sections.push({ key: "me", icon: "home", heading: "HOME", items: buildHomeItems(user) });

  // ── 2-4. Email · Tasks · Calendar ──
  // All three ride the same Office 365 OAuth scope set, so they share one
  // gate (back_office gets none of them).
  if (canSeeEmailSection(user)) {
    sections.push({ key: "email", icon: "mail", heading: "EMAIL", items: EMAIL_DESTINATION_ITEMS });
    sections.push({ key: "tasks", icon: "check-square", heading: "TASKS", items: TASKS_ITEMS });
    sections.push({
      key: "calendar",
      icon: "calendar",
      heading: "CALENDAR",
      items: CALENDAR_ITEMS,
    });
  }

  // ── 5. Marketing ── every employee (the pages scope themselves).
  sections.push({
    key: "marketing",
    icon: "megaphone",
    heading: "MARKETING",
    items: [],
    subgroups: buildMarketingSubgroups(user),
  });

  // ── 6. Automations ── open to every employee; looking at what fires
  // is not an authoring permission.
  if (user?.employee_role_slug) {
    sections.push({
      key: "automations",
      icon: "workflow",
      heading: "AUTOMATIONS",
      items: buildAutomationItems(user),
    });
  }

  // ── 7. Forms · 8. E-sign ── every employee, no gatekeeper.
  sections.push({ key: "forms", icon: "clipboard-list", heading: "FORMS", items: FORMS_ITEMS });
  sections.push({ key: "esign", icon: "pen", heading: "E-SIGN", items: ESIGN_ITEMS });

  // ── 9. Company tools ──
  sections.push({
    key: "company",
    icon: "building",
    heading: "COMPANY TOOLS",
    railLabel: "Company",
    items: [],
    subgroups: buildCompanySubgroups(user),
  });

  // ── 10. Admin ──
  if (canSeeAdminSection(user)) {
    sections.push({
      key: "admin",
      icon: "settings",
      heading: "ADMIN",
      items: [],
      subgroups: ADMIN_SUBGROUPS.map((g) => ({
        ...g,
        // Staging Outbox (2026-11-02): system admins only, the page's own
        // gate (compliance shares this section but not that page). On
        // production the page says the outbox exists only on staging.
        items:
          g.heading === "System" && canSeeTeamPage(user)
            ? [...g.items, { label: "Staging Outbox", to: "/admin/staging-outbox", icon: "outbox" }]
            : g.items,
        startFolded: true,
      })),
    });
  }

  return sections;
}

// ── The phone drawer's CLIENTS section ──────────────────────────────
//
// On desktop the six primary record lanes (Contacts / Households /
// Services / Opportunities / Processing / Ongoing Care) live in the top
// menu bar, so the rail deliberately never repeats them. On a phone the
// top bar's menu is hidden (≤500px takeover) and the hamburger drawer
// renders THIS module's sections — which left the lanes with no menu
// entry at all (Grant hit it 2026-08-28: "how do people navigate to
// opportunities, processing and ongoing care?"). Only the one
// role-specific bottom tab and search reached them, breaking the phone
// shell's own rule that the drawer keeps every destination reachable.
//
// So the MOBILE drawer (and only it) prepends this section, built from
// the same role-filtered list the desktop top bar renders — the two can
// never drift apart. It borrows the "me" key the same way PINNED does:
// drawer folds are keyed on heading text, and no rail icon exists on a
// phone, so the closed SectionKey union stays closed.
const PRIMARY_LANE_ICONS: Record<string, IconName> = {
  "/contacts": "user",
  "/households": "home",
  "/service-records": "list",
  "/opportunities": "circle-dot",
  "/processing": "columns",
  "/ongoing-care": "activity",
};

export function buildMobileClientsSection(user: RoleAwareUser): SidebarSection {
  return {
    key: "me",
    icon: "users",
    heading: "CLIENTS",
    items: visibleNavItems(user).map((item) => ({
      label: item.label,
      to: item.to,
      icon: PRIMARY_LANE_ICONS[item.to] ?? "list",
    })),
  };
}

// True when the supplied path activates the supplied sidebar item.
// Exact match OR child path (so /admin/team highlights when on
// /admin/team/anything-deeper).
export function sidebarItemIsActive(pathname: string, itemTo: string): boolean {
  if (pathname === itemTo) return true;
  return pathname.startsWith(itemTo + "/");
}

// Which rail section "owns" the current page — so the icon can show where
// you are, independent of which pane happens to be open (2026-08-24
// audit: the rail only ever lit up the OPEN section; a page you were
// actually looking at gave no signal at all once its pane was closed,
// while the pinned icons below it DID show location — one strip,
// disagreeing with itself).
//
// EMAIL is a special case: its pane is drawn by the live folder tree
// (EmailSidebarSection), not by EMAIL_DESTINATION_ITEMS, so a folder or
// search URL (`/email/folders/…`, `/email/search`) has no entry in the
// data to match against. Anything under `/email/` belongs to EMAIL.
export function sectionForPath(
  pathname: string,
  sections: SidebarSection[],
): SidebarSection | null {
  if (pathname === "/email" || pathname.startsWith("/email/")) {
    return sections.find((s) => s.key === "email") ?? null;
  }
  for (const section of sections) {
    if (section.items.some((item) => sidebarItemIsActive(pathname, item.to))) {
      return section;
    }
    for (const group of section.subgroups ?? []) {
      if (group.items.some((item) => sidebarItemIsActive(pathname, item.to))) {
        return section;
      }
    }
  }
  return null;
}
